<template>
  <div ref="stage" class="flex h-full w-full items-center justify-center overflow-hidden">
    <div :style="{ width: `${DEVICE.width * scale}px`, height: `${DEVICE.height * scale}px` }">
      <div class="iphone relative origin-top-left" :style="{ width: `${DEVICE.width}px`, height: `${DEVICE.height}px`, transform: `scale(${scale})` }">
        <span class="iphone-button left-[-3px] top-[150px] h-[32px]" aria-hidden="true"></span>
        <span class="iphone-button left-[-3px] top-[205px] h-[62px]" aria-hidden="true"></span>
        <span class="iphone-button left-[-3px] top-[280px] h-[62px]" aria-hidden="true"></span>
        <span class="iphone-button right-[-3px] top-[235px] h-[98px]" aria-hidden="true"></span>

        <div class="iphone-screen absolute inset-[12px] flex flex-col overflow-hidden rounded-[52px] bg-white">
          <div class="relative flex h-[54px] shrink-0 items-end justify-between px-[34px] pb-[9px] text-[16px] font-semibold text-black">
            <span class="tabular-nums">{{ time }}</span>
            <span class="absolute left-1/2 top-[11px] h-[35px] w-[122px] -translate-x-1/2 rounded-full bg-black" aria-hidden="true"></span>
            <span class="flex items-center gap-[6px]" aria-hidden="true">
              <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor"><rect x="0" y="8" width="3" height="4" rx=".8"/><rect x="5" y="5.500" width="3" height="6.500" rx=".8"/><rect x="10" y="3" width="3" height="9" rx=".8"/><rect x="15" y="0" width="3" height="12" rx=".8"/></svg>
              <svg width="17" height="12" viewBox="0 0 17 12" fill="currentColor"><path d="M8.500 2.400c2.400 0 4.600.9 6.200 2.500l1.200-1.200A10.400 10.400 0 0 0 8.500.6 10.400 10.400 0 0 0 1.100 3.700l1.200 1.200a8.700 8.700 0 0 1 6.200-2.500Z"/><path d="M8.500 6.100c1.400 0 2.700.5 3.600 1.500l1.200-1.200a6.800 6.800 0 0 0-9.600 0l1.200 1.200c1-1 2.200-1.500 3.600-1.500Z"/><path d="M8.500 9.300 6.700 9.200l1.800 2.300 1.800-2.300a2.500 2.500 0 0 0-1.800.1Z"/></svg>
              <svg width="27" height="13" viewBox="0 0 27 13" fill="none"><rect x=".5" y=".5" width="23" height="12" rx="3.500" stroke="currentColor" stroke-opacity=".4"/><rect x="2" y="2" width="20" height="9" rx="2" fill="currentColor"/><path d="M25 4.500v4c.8-.3 1.500-1.100 1.500-2s-.7-1.700-1.500-2Z" fill="currentColor" fill-opacity=".5"/></svg>
            </span>
          </div>

          <div class="flex h-[44px] shrink-0 items-center justify-between px-[14px] text-[#3e546c]">
            <span class="flex items-center gap-[4px] text-[17px]">
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" stroke="currentColor" stroke-width="2.400" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2 2 10l8 8"/></svg>
              Deenly
            </span>
            <svg width="16" height="20" viewBox="0 0 16 20" fill="none" stroke="currentColor" stroke-width="1.800" stroke-linejoin="round" aria-hidden="true"><path d="M2 2h12v16l-6-4-6 4V2Z"/></svg>
          </div>

          <div class="iphone-scroll min-h-0 flex-1 overflow-y-auto px-[20px] pb-[60px] pt-[4px]">
            <slot />
          </div>

          <span class="pointer-events-none absolute bottom-[8px] left-1/2 h-[5px] w-[139px] -translate-x-1/2 rounded-full bg-black" aria-hidden="true"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';

/** An iPhone at its true size (393 × 852 points plus the frame), scaled down as one piece to fit. */
const DEVICE = { width: 417, height: 876 };
const MARGIN = 28;

const stage = ref(null);
const scale = ref(0.8);
const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

let observer = null;

onMounted(() => {
  observer = new ResizeObserver(([entry]) => {
    const { width, height } = entry.contentRect;
    scale.value = Math.min(1, (width - MARGIN) / DEVICE.width, (height - MARGIN) / DEVICE.height);
  });
  observer.observe(stage.value);
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.iphone {
  border-radius: 64px;
  background: linear-gradient(145deg, #4a4d52, #1d1e21 35%, #17181a 70%, #3c3e43);
  box-shadow: 0 0 0 1.500px #55585e inset, 0 30px 60px -18px rgba(15, 44, 44, 0.45), 0 12px 24px -12px rgba(15, 44, 44, 0.3);
}
.iphone-screen {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  box-shadow: 0 0 0 4px #000;
}
.iphone-button {
  position: absolute;
  width: 3px;
  border-radius: 2px;
  background: #33353a;
}
.iphone-scroll { scrollbar-width: none; }
.iphone-scroll::-webkit-scrollbar { display: none; }
</style>
